import { isArray, isPlainObject, keys, last, zipObject } from 'lodash'
import Moment from 'moment'
import { extendMoment } from 'moment-range'

import { ISO_DATE_FORMAT } from 'Global/constants'
import { isWellShaped } from 'Global/Helpers/objectHelpers'

const moment = extendMoment(Moment)

/**
 * Returns the correct popup string according to the given reason & event.
 * @param {object} reservation - A reservation.
 * @returns {string} Popup string.
 */
export const buildCurrentPopUpString = reservation => {
  if (isPlainObject(reservation)) {
    if (isWellShaped(reservation, ['event.name', 'reason'])) {
      return `Reserved: #${reservation.event.name} | ${reservation.reason}`
    } else if (isWellShaped(reservation, 'event.name') && !isWellShaped(reservation, 'reason')) {
      return `Reserved: #${reservation.event.name}`
    } else if (!isWellShaped(reservation, 'event.name') && isWellShaped(reservation, 'reason')) {
      return `Reserved: ${reservation.reason}`
    }
  }

  return null
}

/**
 * Returns the current active reservation.
 * @param {object[]} reservations - All reservations.
 * @returns {object} Current active reservation.
 */
export const getCurrent = reservations => {
  if (reservations && typeof reservations === 'object') {
    if (reservations.length) {
      return reservations
        .filter(r => !r.deleted)
        .find(r => {
          if (r.dateFrom && r.dateTo) {
            return moment(r.dateTo).isSameOrAfter(moment(), 'day') && moment(r.dateFrom).isSameOrBefore(moment(), 'day')
          } else {
            return false
          }
        })
    }
  }

  return
}

/**
 * Returns all past reservations.
 * @param {object[]} reservations - All reservations.
 * @returns {object[]} All past reservations.
 */
export const getRecent = reservations => {
  if (reservations && typeof reservations === 'object') {
    if (reservations.length) {
      return reservations.filter(r => !r.deleted).filter(r => moment(r.dateTo).isBefore(moment(), 'day'))
    }
  }

  return []
}

/**
 * Returns all upcoming reservations.
 * @param {object[]} reservations - All reservations.
 * @returns {object[]} All upcoming reservations.
 */
export const getUpcoming = reservations => {
  if (reservations && typeof reservations === 'object') {
    if (reservations.length) {
      return reservations.filter(r => !r.deleted).filter(r => moment(r.dateFrom).isAfter(moment(), 'day'))
    }
  }

  return []
}

/**
 * Returns all reserved dates.
 * @param {object[]} reservations - All reservations.
 * @param {number} [ignored] - Ignored reservation (optional).
 * @returns {string[]} Datestrings of already reserved dates.
 */
export const getReservedDates = (reservations, ignored) => {
  const currentAndUpcoming = getCurrentAndUpcoming(reservations).filter(r => r.id !== ignored)

  const reservedDates = {}

  for (const r of currentAndUpcoming) {
    if (isWellShaped(r, ['dateFrom', 'dateTo'])) {
      const { dateFrom, dateTo } = r

      const range = moment.range(dateFrom, dateTo)

      for (const day of range.by('days')) {
        reservedDates[day.format(ISO_DATE_FORMAT)] = true
      }
    }
  }

  return keys(reservedDates)
}

/**
 * Returns all current and upcoming reservations of a rug.
 * @param {array} reservations
 * @returns {array} - All current and upcoming reservations.
 */
export const getCurrentAndUpcoming = reservations => {
  const current = getCurrent(reservations)
  if (current) {
    return [current, ...getUpcoming(reservations)]
  } else {
    return getUpcoming(reservations)
  }
}

/**
 * Returns the first day where no reservation is active.
 * @param {object[]} reservations - All reservations.
 * @param {number} [ignored] - Ignored reservation (optional).
 * @returns {string} Formatted string of first allowed day.
 */
export const getFirstAllowedDate = (reservations, ignored) => {
  const reservedDatesArray = getReservedDates(reservations, ignored)

  const reservedDates = zipObject(reservedDatesArray, reservedDatesArray)

  const range = moment.range(moment(), moment(last(reservedDatesArray)))

  for (const day of range.by('days')) {
    const formattedDay = moment(day).format(ISO_DATE_FORMAT)

    if (!reservedDates[formattedDay]) {
      return moment(day).format(ISO_DATE_FORMAT)
    }
  }
  // TODO: This isn't optimal yet.
  return moment().format(ISO_DATE_FORMAT)
}

/**
 * Returns the last day before the start day of another reservation
 * or eight weeks after the start date.
 * Returns undefined if startDate is undefined.
 * @param {?string} [startDate] - Reservation start date.
 * @param {object[]} reservations - All reservations.
 * @param {?number} ignored - Ignored reservation (optional).
 * @returns {object} Formatted string of last allowed day.
 */
export const getLastAllowedReserveDate = (startDate, reservations, ignored) => {
  if (!startDate) {
    return
  }

  const reservedDatesArray = getReservedDates(reservations, ignored)

  const reservedDates = zipObject(reservedDatesArray, reservedDatesArray)

  const range = moment.range(startDate, moment(startDate).add(8, 'weeks'))

  for (const day of range.by('days')) {
    const formattedDay = moment(day).format(ISO_DATE_FORMAT)

    if (reservedDates[formattedDay]) {
      return moment(day)
        .subtract(1, 'day')
        .format(ISO_DATE_FORMAT)
    }
  }

  return moment(startDate)
    .add(8, 'weeks')
    .format(ISO_DATE_FORMAT)
}

/**
 * Returns whether the rug is available at the moment.
 * @param {object[]} reservations - All reservations.
 * @returns {boolean} Availability
 */
export const isAvailable = reservations => {
  if (getCurrent(reservations)) {
    return false
  } else {
    return true
  }
}

/**
 * Returns whether the rug is available in a specific date range.
 * @param {object[]} reservations - All reservations.
 * @param {string} min - Date range from as ISO-Date formatted string.
 * @param {string?} max - Date range until as ISO-Date formatted string.
 * @returns {boolean} Availability
 */
export const isAvailableRange = (reservations, min, max) => {
  if (isArray(reservations)) {
    const maxValid = (moment(max, ISO_DATE_FORMAT, true).isValid() && max && typeof min === 'string') || min === ''
    const minValid = (moment(min, ISO_DATE_FORMAT, true).isValid() && min && typeof min === 'string') || max === ''

    let available = true

    for (const r of reservations.filter(r => !r.deleted)) {
      if (minValid && maxValid) {
        const rangeFrom = moment(min)
        const rangeTo = moment(max)

        if (moment(r.dateFrom).isBetween(rangeFrom, rangeTo, 'day', '[]') || moment(r.dateTo).isBetween(rangeFrom, rangeTo, 'day', '[]')) {
          available = false
          break
        }
      } else if (minValid && !maxValid) {
        const rangeFrom = moment(min)

        if (moment(r.dateTo).isSameOrAfter(rangeFrom, 'day')) {
          available = false
          break
        }
      }
    }

    return available
  }

  return true
}

/**
 * Returns the next reservations of an rug.
 * @param {array} reservations
 * @param {object} reservation
 * @returns {array} Updated Reservations
 */
export const updateReservations = (reservations, reservation) => {
  if (isArray(reservations)) {
    if (isWellShaped(reservation, 'id')) {
      const filteredReservations = reservations.filter(r => {
        return r.id !== reservation.id
      })

      return [...filteredReservations, reservation]
    } else {
      return reservations
    }
  } else if (isWellShaped(reservation, 'id')) {
    return [reservation]
  }

  return []
}

/**
 * Returns whether a user has a current or upcoming reservation.
 * @param {object} user
 * @param {array} reservations
 * @returns {boolean} - User has reservation.
 */
export const userHasReservations = (user, reservations) => {
  if (isWellShaped(user, 'id') && isArray(reservations)) {
    let hasReservations = false

    for (const r of reservations) {
      if (isWellShaped(r, 'createdBy')) {
        const { createdBy, updatedBy } = r

        if (createdBy.id === user.id) {
          hasReservations = true
        }

        if (updatedBy) {
          if (updatedBy.id === user.id) {
            hasReservations = true
          }
        }
      }
    }

    return hasReservations
  }

  return false
}
